import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../components/Logo";
import ProgressSteps from "../../../components/ProgressSteps";
import NipDigitInput from "../../../components/NipDigitInput";
import CountryPhoneInput from "../../../components/CountryPhoneInput";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { RequiredMark } from "../../../components/RequiredMark";
import {
  getBeninArrondissements,
  getBeninCommunes,
  getBeninDepartements,
  type BeninArrondissement,
  type BeninCommune,
  type BeninDepartement,
} from "../../../api/publicBenin";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const anipRegex = /^\d{10}$/;

/** Bénin (+229) garde son format historique "01" + 8 chiffres ; les autres pays acceptent 6 à 12 chiffres. */
function isNationalNumberValid(dialCode: string, national: string): boolean {
  if (dialCode === "229") return /^01\d{8}$/.test(national);
  return national.length >= 6 && national.length <= 12;
}

/** Groupe l'affichage par 2 chiffres ("01 23 45 67 89") pour faciliter la lecture. */
function groupByTwo(digits: string): string {
  return digits.replace(/(\d{2})(?=\d)/g, "$1 ");
}

export default function ProSignupCredentials() {
  const navigate = useNavigate();
  const [anip, setAnip] = useState("");
  const [dialCode, setDialCode] = useState("229");
  const [nationalPhone, setNationalPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Localisation Bénin (référentiel /api/public/benin/...) — 3 selects en cascade, même pattern
  // que AddPatient.tsx (dashboard professionnel/établissement).
  const [departementId, setDepartementId] = useState("");
  const [communeNom, setCommuneNom] = useState("");
  const [arrondissementNom, setArrondissementNom] = useState("");
  const [departements, setDepartements] = useState<BeninDepartement[]>([]);
  const [communes, setCommunes] = useState<BeninCommune[]>([]);
  const [arrondissements, setArrondissements] = useState<BeninArrondissement[]>([]);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingArrondissements, setLoadingArrondissements] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);

  const departementNom = useMemo(
    () => departements.find((d) => d.id === departementId)?.nom ?? "",
    [departements, departementId]
  );

  // Restauration cross-étapes (localStorage) : commune/arrondissement n'existent qu'une fois la
  // liste parente chargée (cascade asynchrone), donc on les garde en attente ici et on les
  // applique dès que la liste correspondante arrive.
  const pendingCommune = useRef<string | null>(null);
  const pendingArrondissement = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;
    getBeninDepartements().then((result) => {
      if (!mounted) return;
      if (result.ok) setDepartements(result.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!departementId) {
      setCommunes([]);
      setCommuneNom("");
      setArrondissements([]);
      setArrondissementNom("");
      return () => {
        mounted = false;
      };
    }
    setLoadingCommunes(true);
    setAddressError(null);
    setCommuneNom("");
    setArrondissements([]);
    setArrondissementNom("");
    getBeninCommunes(departementId)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setCommunes([]);
          setAddressError(result.message || "Impossible de charger les communes.");
          return;
        }
        setCommunes(result.data);
        if (pendingCommune.current && result.data.some((c) => c.nom === pendingCommune.current)) {
          setCommuneNom(pendingCommune.current);
        }
        pendingCommune.current = null;
      })
      .finally(() => {
        if (mounted) setLoadingCommunes(false);
      });
    return () => {
      mounted = false;
    };
  }, [departementId]);

  useEffect(() => {
    let mounted = true;
    if (!departementId || !communeNom) {
      setArrondissements([]);
      setArrondissementNom("");
      return () => {
        mounted = false;
      };
    }
    setLoadingArrondissements(true);
    setAddressError(null);
    setArrondissementNom("");
    getBeninArrondissements(departementId, communeNom)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setArrondissements([]);
          setAddressError(result.message || "Impossible de charger les arrondissements.");
          return;
        }
        setArrondissements(result.data);
        if (pendingArrondissement.current && result.data.some((a) => a.nom === pendingArrondissement.current)) {
          setArrondissementNom(pendingArrondissement.current);
        }
        pendingArrondissement.current = null;
      })
      .finally(() => {
        if (mounted) setLoadingArrondissements(false);
      });
    return () => {
      mounted = false;
    };
  }, [departementId, communeNom]);

  const emailValid = useMemo(() => emailRegex.test(email.trim()), [email]);
  const strongPassword = useMemo(() => passwordRegex.test(password), [password]);
  const phoneValid = useMemo(() => isNationalNumberValid(dialCode, nationalPhone), [dialCode, nationalPhone]);
  const phone = useMemo(() => `+${dialCode} ${groupByTwo(nationalPhone)}`.trim(), [dialCode, nationalPhone]);
  const anipValid = anipRegex.test(anip);
  const canProceed =
    departementId !== "" &&
    communeNom !== "" &&
    arrondissementNom !== "" &&
    anipValid &&
    phoneValid &&
    emailValid &&
    strongPassword;

  useEffect(() => {
    try {
      const raw = localStorage.getItem("pro.signup");
      if (raw) {
        const draft = JSON.parse(raw) as Record<string, unknown>;
        if (typeof draft.anip === "string") setAnip(draft.anip);
        if (typeof draft.commune === "string") pendingCommune.current = draft.commune;
        if (typeof draft.arrondissement === "string") pendingArrondissement.current = draft.arrondissement;
        if (typeof draft.departementId === "string") setDepartementId(draft.departementId);
        if (typeof draft.phone === "string") {
          const match = /^\+(\d+)\s*(.*)$/.exec(draft.phone);
          if (match) {
            setDialCode(match[1]);
            setNationalPhone(match[2].replace(/\D/g, ""));
          }
        }
      }
      const credRaw = sessionStorage.getItem("agbaza.pro.signup.credentials");
      if (credRaw) {
        const cred = JSON.parse(credRaw) as { email?: string; password?: string };
        if (typeof cred.email === "string") setEmail(cred.email);
        if (typeof cred.password === "string") setPassword(cred.password);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canProceed) return;
    try {
      const raw = localStorage.getItem("pro.signup");
      const base = raw ? JSON.parse(raw) : {};
      const emailClean = email.replace(/\s/g, "");
      localStorage.setItem(
        "pro.signup",
        JSON.stringify({
          ...base,
          anip,
          departementId,
          departement: departementNom,
          commune: communeNom,
          arrondissement: arrondissementNom,
          phone,
          email: emailClean,
        })
      );
      sessionStorage.setItem(
        "agbaza.pro.signup.credentials",
        JSON.stringify({
          email: emailClean,
          password,
          confirmPassword: password,
        })
      );
    } catch {
      /* ignore */
    }
    navigate("/professionnel/inscription/etablissements");
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img src={import.meta.env.BASE_URL + 'maps-reset-side.jpg'} alt="pro-signup-credentials" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/30" />

      <div className="absolute inset-0 z-10 px-4 md:px-8 py-6 md:py-8 flex">
        <div className="max-w-6xl mx-auto flex flex-col w-full">
          <div className="pl-2 md:pl-6 pt-2 md:pt-4">
            <Link to="/professionnel/inscription/informations-1" className="inline-flex items-center gap-2 text-white/90">
              <ArrowLeft size={18} /> Retour
            </Link>
          </div>

          <div className="mt-auto flex items-end justify-between gap-6 pl-2 md:pl-6 pr-4 md:pr-8 pb-4 md:pb-8">
            <div className="w-full max-w-2xl min-h-[520px] bg-white/95 backdrop-blur rounded-[12px] shadow p-8 md:p-10">
              <h1 className="text-2xl font-semibold" style={{ color: '#0B1F16' }}>Créez votre compte</h1>

              <form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    ANIP
                    <RequiredMark />
                  </label>
                  <NipDigitInput
                    value={anip}
                    onChange={setAnip}
                    invalid={anip.length > 0 && !anipValid}
                  />
                </div>

                {addressError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                    {addressError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm" style={{ color: '#0B1F16' }}>
                      Département
                      <RequiredMark />
                    </label>
                    <select
                      value={departementId}
                      onChange={(e) => setDepartementId(e.target.value)}
                      className="w-full h-14 rounded-[10px] border-2 px-3 bg-white text-base"
                      style={{ borderColor: '#D1E2DD' }}
                    >
                      <option value="" disabled>Choisir…</option>
                      {departements.map((d) => (
                        <option key={d.id} value={d.id}>{d.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm" style={{ color: '#0B1F16' }}>
                      Commune
                      <RequiredMark />
                    </label>
                    <select
                      value={communeNom}
                      onChange={(e) => setCommuneNom(e.target.value)}
                      disabled={!departementId || loadingCommunes}
                      className="w-full h-14 rounded-[10px] border-2 px-3 bg-white text-base disabled:opacity-60"
                      style={{ borderColor: '#D1E2DD' }}
                    >
                      <option value="" disabled>
                        {!departementId ? "Choisir d'abord un département" : loadingCommunes ? "Chargement…" : "Choisir…"}
                      </option>
                      {communes.map((c) => (
                        <option key={`${c.id ?? c.nom}-${c.nom}`} value={c.nom}>{c.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm" style={{ color: '#0B1F16' }}>
                      Arrondissement
                      <RequiredMark />
                    </label>
                    <select
                      value={arrondissementNom}
                      onChange={(e) => setArrondissementNom(e.target.value)}
                      disabled={!communeNom || loadingArrondissements}
                      className="w-full h-14 rounded-[10px] border-2 px-3 bg-white text-base disabled:opacity-60"
                      style={{ borderColor: '#D1E2DD' }}
                    >
                      <option value="" disabled>
                        {!communeNom ? "Choisir d'abord une commune" : loadingArrondissements ? "Chargement…" : "Choisir…"}
                      </option>
                      {arrondissements.map((a) => (
                        <option key={`${a.id ?? a.nom}-${a.nom}`} value={a.nom}>{a.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Téléphone
                    <RequiredMark />
                  </label>
                  <CountryPhoneInput
                    dialCode={dialCode}
                    national={nationalPhone}
                    onDialCodeChange={setDialCode}
                    onNationalChange={setNationalPhone}
                    invalid={nationalPhone.length > 0 && !phoneValid}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Email
                    <RequiredMark />
                  </label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))} placeholder="exemple@company.com" className={`w-full h-14 rounded-[10px] border-2 px-3 text-base ${emailValid || email === '' ? '' : 'border-red-400'}`} style={{ borderColor: emailValid || email === '' ? '#D1E2DD' : '#F87171' }} />
                </div>

                <div className="space-y-2">
                  <label className="text-sm" style={{ color: '#0B1F16' }}>
                    Mot de passe
                    <RequiredMark />
                  </label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full h-14 rounded-[10px] border-2 px-3 text-base ${strongPassword || password === '' ? '' : 'border-red-400'}`} style={{ borderColor: strongPassword || password === '' ? '#D1E2DD' : '#F87171' }} />
                  <div className="text-xs text-gray-600">Au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial.</div>
                </div>

                <button type="submit" className="w-full h-14 rounded-[10px] font-medium cursor-pointer" style={{ backgroundColor: canProceed ? '#00553E' : '#DCEFE9', color: canProceed ? '#FFFFFF' : '#7aa79a' }} disabled={!canProceed}>
                  Suivant
                </button>
              </form>
            </div>

            <div className="hidden md:block shrink-0">
              <img src={import.meta.env.BASE_URL + 'Group 5.png'} alt="agabaza-logo" className="w-auto" style={{ height: 144 }} />
            </div>
          </div>

          <div className="pl-2 md:pl-6 pt-2 md:pt-4">
            <ProgressSteps step={2} total={4} />
          </div>
        </div>
      </div>
    </div>
  );
}
